//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import twitterText from 'twitter-text';

import { FB_REVENUE_ATTRIBUTION_HELP_LINK } from '@account-settings/redux/facebook-instagram/facebookInstagramSettingsConstants';
import ActionButton from '@components/atoms/action-button/ActionButton';
import { Alert } from '@components/atoms/alert/Alert';
import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import { InlineSpinner } from '@components/atoms/spinner/Spinner';
import TextInput from '@components/atoms/text-input/TextInput';
import { Modal } from '@components/molecules/modal/Modal';
import { useSmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { SmartInputTrackingId } from '@components/molecules/smart-input/smartInputTrackingIds';
import { getSmartInputTrackingId } from '@components/molecules/smart-input/smartInputUtils';
import { KEYS } from '@constants/keyboard';
import {
  SHORTLINK_MODAL_MESSAGE_DEFAULT,
  SHORTLINK_MODAL_MESSAGE_REVENUE,
} from '@facebook/constants/facebookPostDetailsUIConstants';
import { Mixins } from '@theme/mixins';
import { isDefined } from '@utils/filterPredicates';
import { keyEquals } from '@utils/keyboardUtils';
import {
  getUrlWithSecureProtocol,
  getUrlWithoutProtocol,
} from '@utils/urlUtils';
import isValidUrl from '@utils/urlValidation';

interface IOwnProps {
  closeModal: () => void;
  handleInsertShortLink: (shortLink: string) => Promise<void>;
  initialOriginalLinkValue?: string;
  initialShortLinkValue?: string;
  isEditLinkModal: boolean;
  isOpen: boolean;
}

type Props = IOwnProps;

const ADD_LINK_MODAL_TITLE_TEXT = 'Add Short Link';
const ADD_LINK_MODAL_PRIMARY_BTN_TEXT = 'Add Short Link';
const EDIT_LINK_MODAL_TITLE_TEXT = 'Edit Short Link';
const EDIT_LINK_MODAL_PRIMARY_BTN_TEXT = 'Update';

/**
 * Check if the original link value matches both Twitter's URL validation
 * and Curalate's URL validation (so it can be used to get a short link).
 * Attempt to add a `https://` protocol if the original link value doesn't
 * already have one
 *
 * @param originalLinkValue value of the link to validate
 */
const getValidOriginalLink = (
  originalLinkValue: string,
): string | undefined => {
  // TODO: Replace this validation with unified link strategy
  const trimmedOriginalLinkValue = originalLinkValue.trim();
  // Validate that the original link value is a valid url to both Twitter and Curalate's url validation
  const extractedUrls = twitterText.extractUrls(trimmedOriginalLinkValue);
  const isValidUrlToTwitter = extractedUrls.length === 1 && extractedUrls[0];

  const isValidUrlToCuralate = isValidUrl(trimmedOriginalLinkValue);

  if (isValidUrlToTwitter && isValidUrlToCuralate) {
    // if original link is valid url but doesn't have a protocal, attempt to add one
    const urlWithProtocol = getUrlWithSecureProtocol(trimmedOriginalLinkValue);

    return urlWithProtocol;
  }
};

export const LinkShorteningModal: React.FunctionComponent<Props> = ({
  closeModal,
  handleInsertShortLink,
  initialOriginalLinkValue = '',
  initialShortLinkValue = '',
  isEditLinkModal,
  isOpen,
}) => {
  const smartInputContext = useSmartInputContext();
  const [originalLinkValue, setOriginalLinkValue] = useState<string>(
    initialOriginalLinkValue,
  );
  const [previousOriginalLinkValue, setPreviousOriginalLinkValue] =
    useState<string>(initialOriginalLinkValue);
  const [originalLinkValidationError, setOriginalLinkValidationError] =
    useState<string>('');

  const [hasErrorGettingShortLink, setHasErrorGettingShortLink] =
    useState(false);
  const [shortLinkValue, setShortLinkValue] = useState<string>(
    initialShortLinkValue,
  );
  const [isLoadingShortLink, setIsLoadingShortLink] = useState(false);
  const [shouldShowShortLinkInput, setShouldShowShortLinkInput] = useState(
    !!initialShortLinkValue,
  );

  const originalLinkInputRef = useRef<HTMLInputElement | null>(null);

  // Focus original link input on first render
  useEffect(() => {
    if (originalLinkInputRef.current) {
      originalLinkInputRef.current.focus();
    }
  }, []);

  // If creating a short link from existing original link, start getting a short link on first render
  useEffect(() => {
    if (!!initialOriginalLinkValue && !initialShortLinkValue) {
      void handleShortenLink();
    }
  }, []);

  // Hide the previous short link fetching error when the user updates the original link
  useEffect(() => {
    if (hasErrorGettingShortLink) {
      setHasErrorGettingShortLink(false);
      setShouldShowShortLinkInput(false);
    }
  }, [originalLinkValue]);

  // If the original link changes then hide the short link input and clear value
  useEffect(() => {
    if (previousOriginalLinkValue !== originalLinkValue) {
      setShouldShowShortLinkInput(false);
      setShortLinkValue('');
    }
  }, [previousOriginalLinkValue, originalLinkValue]);

  const isPrimaryModalBtnDisabled =
    hasErrorGettingShortLink ||
    isLoadingShortLink ||
    !!originalLinkValidationError ||
    !originalLinkValue ||
    !shortLinkValue;

  const handleShortenLink = async () => {
    if (originalLinkValue === '') {
      // Reset state
      setOriginalLinkValue('');
      setHasErrorGettingShortLink(false);
      setShortLinkValue('');
      setPreviousOriginalLinkValue('');
      setIsLoadingShortLink(false);
      setShouldShowShortLinkInput(false);

      return;
    }

    const validatedOriginalLink = getValidOriginalLink(originalLinkValue);

    if (!validatedOriginalLink) {
      setOriginalLinkValidationError(
        'Please input a valid url (http(s)://www.example.com)',
      );

      return;
    }

    // Only generate short link if we have a validatedOriginalLink
    // and if we don't already have a short link or the original link has changed
    if (!shortLinkValue || originalLinkValue !== previousOriginalLinkValue) {
      // Reset error states & short link value
      setOriginalLinkValidationError('');
      setHasErrorGettingShortLink(false);
      setShortLinkValue('');

      // Save value as new previous original link value
      setPreviousOriginalLinkValue(originalLinkValue);

      setIsLoadingShortLink(true);
      setShouldShowShortLinkInput(true);

      const shortLink = await smartInputContext.linkShortening?.getShortLink(
        validatedOriginalLink,
      );

      if (shortLink) {
        const shortLinkWithoutProtocol = getUrlWithoutProtocol(shortLink);
        setShortLinkValue(shortLinkWithoutProtocol);
      } else {
        setHasErrorGettingShortLink(true);
        setShortLinkValue('');
      }

      setIsLoadingShortLink(false);
      return;
    }
  };

  const getModalText = (isRevenueTrackingEnabled: boolean) => {
    return isRevenueTrackingEnabled ? (
      <>
        {`${SHORTLINK_MODAL_MESSAGE_REVENUE} `}
        <PrimaryAnchor
          href={FB_REVENUE_ATTRIBUTION_HELP_LINK}
          rel='noreferrer'
          target='_blank'
        >
          Learn more
        </PrimaryAnchor>
      </>
    ) : (
      <>{SHORTLINK_MODAL_MESSAGE_DEFAULT}</>
    );
  };

  const handleInsertShortLinkAndCloseModal = async () => {
    await handleInsertShortLink(shortLinkValue);
    closeModal();
  };

  const formTitleId = `form-title-${getSmartInputTrackingId(
    smartInputContext.trackingIdPrefix,
    SmartInputTrackingId.BaseInput,
  )}`;

  const content = (
    <Form
      aria-labelledby={formTitleId}
      onSubmit={async (e) => {
        // Prevent native form submission
        e.preventDefault();

        if (!isPrimaryModalBtnDisabled) {
          await handleInsertShortLinkAndCloseModal();
        }
      }}
    >
      <FormTitle aria-level={2} id={formTitleId} role='heading'>
        {getModalText(
          !!smartInputContext.linkShortening?.isRevenueTrackingEnabled,
        )}
      </FormTitle>
      <OriginalLinkInput
        ref={originalLinkInputRef}
        errorText={originalLinkValidationError}
        hasError={!!originalLinkValidationError}
        helpText={
          shouldShowShortLinkInput
            ? ''
            : 'Add your URL, then hit Enter to generate your short link.'
        }
        label='Link'
        onBlur={handleShortenLink}
        onChange={setOriginalLinkValue}
        onKeyDown={(e) => {
          if (keyEquals(e, KEYS.ENTER)) {
            // prevent the native enter functionality
            e.preventDefault();
            void handleShortenLink();
          }
        }}
        placeholder='https://example.com'
        trackingId={getSmartInputTrackingId(
          smartInputContext.trackingIdPrefix,
          SmartInputTrackingId.ShortLinkModalOriginalLinkInput,
        )}
        value={originalLinkValue}
      />
      {shouldShowShortLinkInput &&
        (hasErrorGettingShortLink ? (
          <Alert type='warning'>
            An error occurred while creating your short link. Please try again
            or contact Bazaarvoice Support.
          </Alert>
        ) : (
          <ShortLinkInputWrapper>
            <ShortLinkInput
              aria-label='Shortened Link'
              isDisabled={true}
              value={shortLinkValue}
            />
            {isLoadingShortLink && <LoadingSpinner />}
            {shortLinkValue && (
              <PreviewShortLinkButton
                aria-label='Open shortened link, opens in new window'
                iconColor={theme.colors.cyan8}
                iconName='linkout'
                label='Preview URL'
                onClick={() => {
                  // TODO: Update ActionButton to optionally be a link
                  window.open(
                    getUrlWithSecureProtocol(shortLinkValue),
                    '_blank',
                  );
                }}
                trackingId={getSmartInputTrackingId(
                  smartInputContext.trackingIdPrefix,
                  SmartInputTrackingId.ShortLinkModalPreviewLinkButton,
                )}
              />
            )}
          </ShortLinkInputWrapper>
        ))}
      {isDefined(smartInputContext.linkShortening?.additionalInfo) && (
        <AdditionalInfoContainer>
          {smartInputContext.linkShortening.additionalInfo}
        </AdditionalInfoContainer>
      )}
    </Form>
  );

  return (
    <Modal
      closeModal={closeModal}
      contents={content}
      isModalOpen={isOpen}
      primaryButtonInfo={{
        isDisabled: isPrimaryModalBtnDisabled,
        onClick: handleInsertShortLinkAndCloseModal,
        buttonContent: isEditLinkModal
          ? EDIT_LINK_MODAL_PRIMARY_BTN_TEXT
          : ADD_LINK_MODAL_PRIMARY_BTN_TEXT,
        trackingId: getSmartInputTrackingId(
          smartInputContext.trackingIdPrefix,
          isEditLinkModal
            ? SmartInputTrackingId.ShortLinkModalUpdateShortLinkButton
            : SmartInputTrackingId.ShortLinkModalAddShortLinkButton,
        ),
      }}
      secondaryButtonInfo={{
        buttonContent: 'Cancel',
        onClick: () => closeModal(),
        trackingId: getSmartInputTrackingId(
          smartInputContext.trackingIdPrefix,
          SmartInputTrackingId.ShortLinkModalCancelButton,
        ),
      }}
      title={
        isEditLinkModal ? EDIT_LINK_MODAL_TITLE_TEXT : ADD_LINK_MODAL_TITLE_TEXT
      }
      trackingId={getSmartInputTrackingId(
        smartInputContext.trackingIdPrefix,
        isEditLinkModal
          ? SmartInputTrackingId.ShortLinkEditLinkModal
          : SmartInputTrackingId.ShortLinkAddLinkModal,
      )}
      width='wide'
    />
  );
};

const Form = styled.form`
  outline: none;
`;

const FormTitle = styled.div(
  ({ theme }) => `
    ${Mixins.Text.Body};
    margin-bottom: ${theme.spacings[3]}; 
  `,
);

const OriginalLinkInput = styled(TextInput)(
  ({ theme }) => `
    margin-bottom: ${theme.spacings[2]};
  `,
);

const ShortLinkInputWrapper = styled.div`
  position: relative;
`;

const ShortLinkInput = styled(TextInput)``;

const LoadingSpinner = styled(InlineSpinner)(
  ({ theme }) => `
    position: absolute;
    top: 50%;
    left: ${theme.spacings[2]};
    transform: translateY(-50%);
  `,
);

const PreviewShortLinkButton = styled(ActionButton)`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
`;

const AdditionalInfoContainer = styled.div(
  ({ theme }) => `
    width: 100%;
    margin-top: ${theme.spacings[2]};
  `,
);
